/*
 * Coleção de ícones do nxvolt.
 *
 * Por que existir: a lista de módulos vinha com emoji no `icon` do catalog
 * compartilhado (🔌 ❤️ 🖥). Emoji renderiza diferente por SO/navegador
 * (Windows manda um bloco quadrado colorido, Android manda outro), e cor
 * fora do sistema num menu que a pessoa varre 50x/dia é o traço mais
 * evidente de "template gerado". Aqui mantemos uma família uniforme,
 * stroke 1.75, cor via currentColor, 24x24 em viewBox.
 *
 * Não substituímos o campo `icon` do @nxvolt/shared para não tocar em
 * telas ainda não migradas; o Sidebar consulta este mapa por `slug` e
 * cai no emoji original só se não houver correspondência (evita menu
 * sem ícone quando um módulo novo entra antes deste arquivo).
 */
import type { ReactNode, SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

// Base compartilhada: viewBox, stroke, linejoin — todo ícone herda daqui.
function Svg({ children, ...props }: IconProps & { children: ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

// -------------------------------------------------------------------
// Ícones — nomeados por slug do MODULES_CATALOG.
// Mantidos simples de propósito: ícone de menu com detalhe excessivo
// desenha "ruído" em altura de 18px e some no dark; melhor forma clara
// do que virtuosismo gráfico.
// -------------------------------------------------------------------

export const IconDashboard = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="3" width="7" height="9" rx="1.5" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" />
    <rect x="14" y="12" width="7" height="9" rx="1.5" />
    <rect x="3" y="16" width="7" height="5" rx="1.5" />
  </Svg>
);

// "Equipamentos" — plug EV estilizado (não um plug tomada).
export const IconChargers = (p: IconProps) => (
  <Svg {...p}>
    <path d="M7 8V5" />
    <path d="M13 8V5" />
    <rect x="5" y="8" width="10" height="7" rx="2" />
    <path d="M10 15v2a3 3 0 0 0 3 3h1a3 3 0 0 0 3-3v-4h2" />
  </Svg>
);

// "Aprovação OCPP" — check em círculo.
export const IconApproval = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12.5 2.5 2.5 4.5-5" />
  </Svg>
);

// "Painel Wall" — grade de monitores.
export const IconWall = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4" width="8" height="7" rx="1.5" />
    <rect x="13" y="4" width="8" height="7" rx="1.5" />
    <rect x="3" y="13" width="8" height="7" rx="1.5" />
    <rect x="13" y="13" width="8" height="7" rx="1.5" />
  </Svg>
);

// "Saúde da Bateria" — bateria com heart-beat; coração emoji era o traço
// mais gritante de IA no menu antigo.
export const IconBattery = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="8" width="15" height="9" rx="2" />
    <path d="M18 11v3" />
    <path d="M21 11v3" />
    <path d="M6 12.5h2l1.2-2 1.6 4 1.2-2h2" />
  </Svg>
);

// "Manutenção" — chave inglesa.
export const IconWrench = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4l-6 6a1.5 1.5 0 0 0 2.1 2.1l6-6a4 4 0 0 0 5.4-5.4l-2.5 2.5-2-2 2.4-2.6z" />
  </Svg>
);

// "Monitor ao Vivo" — antena de sinal.
export const IconLive = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="1.5" />
    <path d="M8.5 8.5a5 5 0 0 0 0 7" />
    <path d="M15.5 15.5a5 5 0 0 0 0-7" />
    <path d="M5.5 5.5a9 9 0 0 0 0 13" />
    <path d="M18.5 18.5a9 9 0 0 0 0-13" />
  </Svg>
);

// "Sessões" — raio.
export const IconBolt = (p: IconProps) => (
  <Svg {...p}>
    <path d="M13 3 4 14h7l-1 7 9-11h-7l1-7z" />
  </Svg>
);

// "Fases de Carga" — gráfico de linha subindo.
export const IconTrend = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 17 9 12l3 3 7-8" />
    <path d="M14 4h5v5" />
  </Svg>
);

// "Logs OCPP" — lista de linhas.
export const IconLogs = (p: IconProps) => (
  <Svg {...p}>
    <path d="M8 6h13" />
    <path d="M8 12h13" />
    <path d="M8 18h13" />
    <circle cx="4" cy="6" r="1" />
    <circle cx="4" cy="12" r="1" />
    <circle cx="4" cy="18" r="1" />
  </Svg>
);

// "Alertas" — sino.
export const IconBell = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 8a6 6 0 0 1 12 0c0 6 3 7 3 7H3s3-1 3-7z" />
    <path d="M10 21a2 2 0 0 0 4 0" />
  </Svg>
);

// "Telemetria Sub." — torre/subestação. Antes: 🗼.
export const IconTower = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 3h6l1 18h-8l1-18z" />
    <path d="M8 9h8" />
    <path d="M8 15h8" />
    <path d="M12 3v18" />
  </Svg>
);

// "Acessos API" — escudo. Antes: 🛡️.
export const IconShield = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6l-8-3z" />
    <path d="m9 12 2 2 4-4" />
  </Svg>
);

// "Veículos" — caminhonete/utilitário.
export const IconTruck = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 7h11v9H3z" />
    <path d="M14 10h4l3 3v3h-7" />
    <circle cx="7.5" cy="17.5" r="1.75" />
    <circle cx="17" cy="17.5" r="1.75" />
  </Svg>
);

// "Motoristas" — id badge.
export const IconIdBadge = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <circle cx="12" cy="10" r="2.5" />
    <path d="M8 17c1-2 3-3 4-3s3 1 4 3" />
    <path d="M10 3h4v3h-4z" />
  </Svg>
);

// "Visão Geral" (financeiro) — cifrão em círculo.
export const IconMoney = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M15 9h-4a1.5 1.5 0 0 0 0 3h2a1.5 1.5 0 0 1 0 3H9" />
    <path d="M12 7v2M12 15v2" />
  </Svg>
);

// "Plano Contratado" — folha/contrato.
export const IconContract = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 3h9l4 4v14H6z" />
    <path d="M14 3v5h5" />
    <path d="M9 13h7M9 17h5" />
  </Svg>
);

// "Rel. de Consumos" — gráfico de barras.
export const IconBars = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 20V10" />
    <path d="M10 20V4" />
    <path d="M16 20v-8" />
    <path d="M22 20H2" />
  </Svg>
);

// "Horário de Ponta" — relógio.
export const IconClock = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Svg>
);

// "Usuários"
export const IconUsers = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20c.8-3.2 3.5-5 6.5-5s5.7 1.8 6.5 5" />
    <circle cx="17" cy="9" r="2.8" />
    <path d="M15 15.2c.6-.2 1.3-.2 2-.2 2.5 0 4.5 1.5 5 4" />
  </Svg>
);

// "Logs do Sistema"
export const IconServer = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="7" rx="1.5" />
    <rect x="3" y="13" width="18" height="7" rx="1.5" />
    <path d="M7 7.5h.01M7 16.5h.01" />
    <path d="M11 7.5h6M11 16.5h6" />
  </Svg>
);

// "Configurações"
export const IconSettings = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.14.34.22.7.22 1.09v.02c0 .39-.08.75-.22 1.09z" />
  </Svg>
);

// Fallback — quando um slug novo entra sem entrada aqui, este quadrado
// discreto indica "sem ícone" sem quebrar o alinhamento vertical do menu.
export const IconGeneric = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4" y="4" width="16" height="16" rx="3" />
    <circle cx="12" cy="12" r="1.5" />
  </Svg>
);

// Utilitários da topbar (não módulos).
export const IconSun = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </Svg>
);

export const IconMoon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20 15a8 8 0 0 1-11-11 8 8 0 1 0 11 11z" />
  </Svg>
);

export const IconLogout = (p: IconProps) => (
  <Svg {...p}>
    <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
    <path d="M10 17l-5-5 5-5" />
    <path d="M5 12h11" />
  </Svg>
);

// Hambúrguer da topbar mobile: três linhas horizontais equidistantes.
export const IconMenu = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 6h16" />
    <path d="M4 12h16" />
    <path d="M4 18h16" />
  </Svg>
);

// X para fechar drawer/modal quando o contexto pede "fechar" e não "voltar".
export const IconClose = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 6l12 12" />
    <path d="M6 18L18 6" />
  </Svg>
);

// Duplas de chevrons — para colapsar/expandir sidebar. "Left" quando a
// sidebar está aberta ("dobrar pra dentro"); "Right" quando fechada.
export const IconChevronsLeft = (p: IconProps) => (
  <Svg {...p}>
    <path d="M11 17l-5-5 5-5" />
    <path d="M18 17l-5-5 5-5" />
  </Svg>
);

export const IconChevronsRight = (p: IconProps) => (
  <Svg {...p}>
    <path d="M13 17l5-5-5-5" />
    <path d="M6 17l5-5-5-5" />
  </Svg>
);

// Bandeira — feature flags (admin). Não usamos emoji 🚩 no menu porque
// renderiza colorido/inconsistente entre plataformas.
export const IconFlag = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 21V4" />
    <path d="M4 4h11l-1.5 3.5L15 11H4" />
  </Svg>
);

// Seta para baixo apoiando num traço: gesto de "salvar/baixar arquivo".
// Usado em botões de export (XLSX/PDF).
export const IconDownload = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 4v12" />
    <path d="M7 11l5 5 5-5" />
    <path d="M5 20h14" />
  </Svg>
);

// Mark do wordmark — bolt inscrito num quadrado com gradiente laranja.
// Recebe id único quando montado várias vezes na mesma página.
export function NxMark({
  size = 28,
  ...rest
}: { size?: number } & SVGProps<SVGSVGElement>) {
  const gid = "nx-mark-grad";
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      aria-hidden="true"
      {...rest}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff7a1a" />
          <stop offset="1" stopColor="#e04e00" />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width="30" height="30" rx="8" fill={`url(#${gid})`} />
      <path d="M17.5 6 10 17.5h5.5L14 26l8-11.5h-5.5L17.5 6z" fill="var(--nx-neutral-0)" />
    </svg>
  );
}

// Mapa slug -> componente. O Sidebar consulta este mapa; se o slug não
// estiver presente, cai no IconGeneric.
export const ICON_BY_SLUG: Record<string, (p: IconProps) => ReactNode> = {
  dashboard: IconDashboard,
  chargers: IconChargers,
  charger_approval: IconApproval,
  charging_wall: IconWall,
  battery_health: IconBattery,
  maintenance: IconWrench,
  live: IconLive,
  sessions: IconBolt,
  charge_phases: IconTrend,
  ocpp_logs: IconLogs,
  alerts: IconBell,
  telemetria_sub: IconTower,
  api_access: IconShield,
  fleet: IconTruck,
  drivers: IconIdBadge,
  financial: IconMoney,
  plano_contratado: IconContract,
  rel_consumo: IconBars,
  horario_ponta: IconClock,
  users: IconUsers,
  system_logs: IconServer,
  settings: IconSettings,
  feature_flags: IconFlag,
};
